// =============================================================================
// The member status panel from §5.1 step 3 — the critical screen.
//
// Shared between Issue and Member detail on purpose. If a member is blocked,
// the reason must read identically wherever staff see it; two screens drifting
// apart is how a front desk ends up telling a parent something different from
// what the system will do.
// =============================================================================

import { el, formatDate } from './dom.js';
import { T } from './strings.js';

/** Full-width, colour-coded eligibility banner. Never a table row (§2.2). */
export function eligibilityBanner(eligibility) {
  const blocks = eligibility?.blocks ?? [];
  const canBorrow = !!eligibility?.can_borrow;
  return el('div', {
    class: `banner ${canBorrow ? 'banner-ok' : 'banner-blocked'}`,
    role: 'status',
  },
    el('strong', { class: 'banner-title' }, canBorrow ? T.canBorrow : T.blocked),
    ...blocks.map((b) => el('p', { class: 'banner-reason' }, b.message)),
  );
}

/** "2 of 3 books out", turning amber once the limit is reached. */
export function slotsLine(used, total) {
  const full = used >= total;
  return el('p', { class: `slots ${full ? 'slots-full' : ''}` },
    full ? T.slotsFull(used, total) : T.slotsUsed(used, total));
}

/**
 * One active loan, with optional Renew and Return actions.
 *
 * Both are real buttons, not underlined links. An underlined word next to a
 * due date reads as a footnote; Return in particular is a transaction, and it
 * was being missed entirely on this screen.
 */
export function loanRow(loan, { onRenew, onReturn, busy, renewing } = {}) {
  return el('div', { class: 'loan-row' },
    el('span', { class: 'loan-title' }, loan.title),
    el('span', { class: loan.is_overdue ? 'pill pill-danger' : 'pill' },
      loan.is_overdue
        ? T.daysOverdue(loan.days_overdue)
        : `${T.dueBack} ${formatDate(loan.due_at)}`),
    (onRenew || onReturn) && el('span', { class: 'row-actions' },
      onReturn && el('button', {
        class: 'btn btn-sm btn-primary', disabled: busy,
        onClick: () => onReturn(loan),
      }, T.returnThis),
      // `busy` disables; only `renewing` relabels. Driving the label from
      // `busy` made every Renew button say "Renewing…" while a RETURN was
      // waiting to be confirmed — the app announcing something it was not
      // doing.
      onRenew && el('button', {
        class: 'btn btn-sm', disabled: busy,
        onClick: () => onRenew(loan),
      }, renewing ? T.renewing : T.renew),
    ),
  );
}
